import React, {useEffect, useMemo, useState} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import {api} from '../../../lib/api';
import {useAuth} from '../../../contexts/AuthContext';

const {FiClock, FiImage, FiMusic, FiPause, FiPlay, FiRotateCcw, FiFastForward} = FiIcons;

const DEFAULT_NAME = 'Your Name';

function LaunchSettings({
  open,
  duration,
  onDuration,
  name,
  onName,
  flyer,
  onFlyer,
  language,
  onLanguage,
  musicEnabled,
  onMusicEnabled,
  volume,
  onVolume,
  running,
  onStart,
  onPause,
  onReset,
  onJumpToPresentation,
}) {
  const {user} = useAuth();
  const minutes = Math.floor(duration / 60);
  const seconds = duration % 60;

  const [flyerUrlDraft, setFlyerUrlDraft] = useState('');
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventSearch, setEventSearch] = useState('');
  const [selectedEventId, setSelectedEventId] = useState('');

  useEffect(() => {
    if (!open || !user?.id) return undefined;
    let cancelled = false;
    (async () => {
      setEventsLoading(true);
      try {
        const list = (await api.pipelineEvents.getForAssociate(user.id, null)) || [];
        if (!cancelled) setEvents(list);
      } catch (err) {
        console.error(err);
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, user?.id]);

  const filteredEvents = useMemo(() => {
    const q = eventSearch.trim().toLowerCase();
    if (!q) return events;
    return events.filter((e) => (e.title || '').toLowerCase().includes(q));
  }, [events, eventSearch]);

  const handleFile = event => {
    const file = event.target.files?.[0];
    if (file) onFlyer(URL.createObjectURL(file));
  };

  const handleSelectEvent = (eventId) => {
    setSelectedEventId(eventId);
    const ev = events.find((x) => x.id === eventId);
    if (!ev) return;
    onFlyer(ev.hero_image_url || '');
    setFlyerUrlDraft(ev.hero_image_url || '');
    if (!name || name === DEFAULT_NAME) {
      onName(ev.title || DEFAULT_NAME);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.aside
          className="settings-panel"
          initial={{x: '-100%', opacity: 0}}
          animate={{x: 0, opacity: 1}}
          exit={{x: '-100%', opacity: 0}}
          transition={{type: 'spring', damping: 28, stiffness: 260}}
        >
          <div className="settings-brand">
            <span className="brand-symbol">P</span>
            <span><b>Prosperity</b><small>Launch controls</small></span>
            <div className="language-switch">
              <button className={language === 'en' ? 'active' : ''} onClick={() => onLanguage('en')}>EN</button>
              <button className={language === 'es' ? 'active' : ''} onClick={() => onLanguage('es')}>ES</button>
            </div>
          </div>

          <header>
            <span>Prepare your experience</span>
            <h2>Launch settings</h2>
          </header>

          <label className="field">
            <span>Professional name</span>
            <input value={name} onChange={event => onName(event.target.value)} />
          </label>

          <section className="setting-section">
            <h3><SafeIcon icon={FiClock} /> Countdown duration</h3>
            <div className="duration-fields">
              <label>
                <input
                  type="number"
                  min="0"
                  max="99"
                  value={minutes}
                  onChange={event => onDuration(Number(event.target.value) * 60 + seconds)}
                />
                <span>Minutes</span>
              </label>
              <b>:</b>
              <label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={seconds}
                  onChange={event => onDuration(minutes * 60 + Number(event.target.value))}
                />
                <span>Seconds</span>
              </label>
            </div>
          </section>

          <section className="setting-section">
            <h3><SafeIcon icon={FiImage} /> Event flyer</h3>
            <div className="flyer-options">
              <label className="field">
                <span>Image URL</span>
                <input
                  type="url"
                  value={flyerUrlDraft}
                  placeholder="Paste an image URL…"
                  onChange={event => {
                    const value = event.target.value;
                    setFlyerUrlDraft(value);
                    onFlyer(value);
                    setSelectedEventId('');
                  }}
                />
              </label>
              <label className="upload-field">
                <SafeIcon icon={FiImage} />
                <span>{flyer ? 'Replace flyer' : 'Choose an image'}</span>
                <input type="file" accept="image/*" onChange={handleFile} />
              </label>
              <div>
                <input
                  type="search"
                  value={eventSearch}
                  onChange={event => setEventSearch(event.target.value)}
                  placeholder={language === 'es' ? 'Buscar eventos…' : 'Search published events…'}
                />
                {eventsLoading ? (
                  <small>{language === 'es' ? 'Cargando eventos…' : 'Loading events…'}</small>
                ) : (
                  <select
                    value={selectedEventId}
                    onChange={event => handleSelectEvent(event.target.value)}
                    style={{marginTop: 8}}
                  >
                    <option value="">
                      {language === 'es' ? 'Usar un evento publicado…' : 'Use a published event…'}
                    </option>
                    {filteredEvents.map(ev => (
                      <option key={ev.id} value={ev.id}>
                        {ev.title || ev.id}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </section>

          <section className="setting-section">
            <h3><SafeIcon icon={FiMusic} /> Welcome music</h3>
            <div className="music-controls">
              <button
                className={musicEnabled ? 'active' : ''}
                onClick={() => onMusicEnabled(!musicEnabled)}
              >
                {musicEnabled ? 'ON' : 'OFF'}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={event => onVolume(Number(event.target.value))}
              />
            </div>
            <small>Contento — Jorge Luis Chacón</small>
          </section>

          <div className="settings-actions">
            <button className="primary-action" onClick={running ? onPause : onStart}>
              <SafeIcon icon={running ? FiPause : FiPlay} />
              {running ? 'Pause countdown' : 'Start countdown'}
            </button>
            <button className="quiet-action" onClick={onJumpToPresentation}>
              <SafeIcon icon={FiFastForward} />
              {language === 'es' ? 'Ir a la presentación' : 'Jump to presentation'}
            </button>
            <button className="quiet-action" onClick={onReset}>
              <SafeIcon icon={FiRotateCcw} /> Reset
            </button>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}

export default LaunchSettings;
